/**
 * Signed-in app layout: left sidebar (nav + plan usage), top bar (page title, notifications,
 * account menu) and the routed page. Collapses to a drawer below the lg breakpoint.
 */
import { useEffect, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import {
  Bell,
  Bug,
  Crown,
  FolderOpen,
  IndianRupee,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  Palette,
  Plus,
  Settings,
  Wand2,
  X,
} from 'lucide-react';
import { initials, useAuth } from '../../store/authStore';
import { api } from '../../lib/api';
import { ButtonLink } from '../ui/Button';
import { ProgressBar } from '../ui/controls';
import { Logo } from '../ui/Logo';
import { Menu, MenuItem } from '../ui/Menu';

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ size?: number; className?: string }>;
}

export const NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutGrid },
  { to: '/projects', label: 'My Projects', icon: FolderOpen },
  { to: '/templates', label: 'Templates', icon: Wand2 },
  { to: '/create', label: 'Create Caption', icon: Plus },
  { to: '/brand', label: 'Brand Kit', icon: Palette },
  { to: '/billing', label: 'Billing', icon: IndianRupee },
  { to: '/community', label: 'Community', icon: Bug },
  { to: '/settings', label: 'Settings', icon: Settings },
];

const TITLES: Record<string, string> = Object.fromEntries(NAV.map((n) => [n.to, n.label]));

function UsageCard({ onNavigate }: { onNavigate: () => void }) {
  const user = useAuth((s) => s.user);
  const cacheKey = user ? `aicaption:billing-seconds:${user.id}` : null;
  const [availableSeconds, setAvailableSeconds] = useState<number | null>(() => {
    if (!cacheKey) return null;
    try {
      const cached = JSON.parse(sessionStorage.getItem(cacheKey) ?? 'null') as { value?: unknown; savedAt?: unknown } | null;
      return typeof cached?.value === 'number' && typeof cached.savedAt === 'number' && Date.now() - cached.savedAt < 5 * 60_000
        ? cached.value
        : null;
    } catch { return null; }
  });
  useEffect(() => {
    if (!user) return;
    let active = true;
    const refreshBillingUsage = () => void api.billingOverview().then((overview) => {
      if (!active) return;
      setAvailableSeconds(overview.availableSeconds);
      try { sessionStorage.setItem(`aicaption:billing-seconds:${user.id}`, JSON.stringify({ value: overview.availableSeconds, savedAt: Date.now() })); } catch { /* optional cache */ }
    }).catch(() => {
      // Keep a recent cached value. The plan allowance is shown as a stable
      // fallback below rather than leaving the card at Loading forever.
    });
    refreshBillingUsage();
    // A successful checkout must update the shell immediately, without a page
    // reload. The same event also covers wallet-only top-ups.
    window.addEventListener('aicaption:billing-updated', refreshBillingUsage);
    return () => {
      active = false;
      window.removeEventListener('aicaption:billing-updated', refreshBillingUsage);
    };
  }, [user]);
  if (!user) return null;
  const minutes = availableSeconds === null ? null : Math.ceil(availableSeconds / 60);
  const isTrial = user.plan === 'free';
  const planAllowance: Record<string, number> = { starter: 40, creator: 100, pro: 250 };
  const remainingLabel = minutes === null
    ? (isTrial ? '30 min trial' : `${planAllowance[user.plan] ?? 0} min/month`)
    : `${minutes} min left`;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-line bg-canvas p-3.5">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">{isTrial ? 'Free trial' : `${user.plan.charAt(0).toUpperCase()}${user.plan.slice(1)} plan`}</span>
        <span className="tabular text-mute">{remainingLabel}</span>
      </div>
      {isTrial && <ProgressBar value={minutes === null ? 100 : Math.min(100, (minutes / 30) * 100)} />}
      {isTrial && (
        <ButtonLink to="/billing" variant="primary" size="sm" icon={<Crown size={15} />} onClick={onNavigate}>
          Upgrade plan
        </ButtonLink>
      )}
    </div>
  );
}

function Sidebar({ onNavigate }: { onNavigate: () => void }) {
  return (
    <div className="flex h-full flex-col gap-6 p-4">
      <div className="px-1 pt-1">
        <Logo to="/dashboard" />
      </div>
      <nav className="flex flex-col gap-1" aria-label="Main">
        {NAV.map(({ to, label, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onNavigate}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                isActive ? 'bg-soft font-medium text-ink' : 'text-body hover:bg-subtle hover:text-ink'
              }`
            }
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto">
        <UsageCard onNavigate={onNavigate} />
      </div>
    </div>
  );
}

export function AppShell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const [drawer, setDrawer] = useState(false);
  const title = TITLES[pathname] ?? 'AICAPTION';

  return (
    <div className="flex h-full bg-canvas">
      <aside className="hidden w-64 shrink-0 border-r border-line bg-subtle lg:block">
        <Sidebar onNavigate={() => undefined} />
      </aside>

      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <button
            type="button"
            aria-label="Close menu"
            className="absolute inset-0 bg-ink/40"
            onClick={() => setDrawer(false)}
          />
          <aside className="absolute inset-y-0 left-0 w-72 animate-fade-in border-r border-line bg-subtle">
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setDrawer(false)}
              className="absolute top-4 right-3 rounded-md p-1.5 text-mute hover:bg-soft"
            >
              <X size={18} />
            </button>
            <Sidebar onNavigate={() => setDrawer(false)} />
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-16 shrink-0 items-center gap-3 border-b border-line bg-canvas px-4 sm:px-6">
          <button
            type="button"
            aria-label="Open menu"
            onClick={() => setDrawer(true)}
            className="rounded-md p-2 text-body hover:bg-soft lg:hidden"
          >
            <MenuIcon size={20} />
          </button>
          <div className="flex min-w-0 flex-col leading-tight">
            <span className="truncate font-semibold">{title}</span>
            <span className="hidden text-xs text-mute sm:block">Turn every word into a caption.</span>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Menu
              label="Notifications"
              trigger={
                <span className="flex size-9 items-center justify-center rounded-full text-body hover:bg-soft">
                  <Bell size={18} />
                </span>
              }
            >
              {() => <p className="px-3 py-4 text-center text-sm text-mute">You're all caught up.</p>}
            </Menu>
            <Menu
              label="Account"
              trigger={
                <span className="flex size-9 items-center justify-center rounded-full bg-soft text-xs font-semibold text-ink hover:bg-line">
                  {initials(user?.name ?? '')}
                </span>
              }
            >
              {(close) => (
                <>
                  <div className="border-b border-line px-2.5 pt-1.5 pb-2.5">
                    <div className="truncate text-sm font-medium">{user?.name}</div>
                    <div className="truncate text-xs text-mute">{user?.email}</div>
                  </div>
                  <div className="pt-1.5">
                    <MenuItem
                      icon={<Settings size={16} />}
                      onClick={() => {
                        close();
                        navigate('/settings');
                      }}
                    >
                      Settings
                    </MenuItem>
                    <MenuItem
                      danger
                      icon={<LogOut size={16} />}
                      onClick={async () => {
                        close();
                        await logout();
                        navigate('/');
                      }}
                    >
                      Log out
                    </MenuItem>
                  </div>
                </>
              )}
            </Menu>
          </div>
        </header>
        <main className="scroll-thin min-h-0 flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/** Page heading used at the top of every app page. */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
        {subtitle && <p className="mt-1 text-mute">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

/** Standard page padding/width. */
export function Page({
  children,
  narrow,
  compact = false,
}: {
  children: React.ReactNode;
  narrow?: boolean;
  /** Use for short, task-focused flows that should fit in a desktop viewport. */
  compact?: boolean;
}) {
  return (
    <div
      className={`mx-auto flex w-full flex-col px-4 sm:px-8 ${
        compact ? 'gap-5 py-6' : 'gap-8 py-8'
      } ${narrow ? 'max-w-3xl' : 'max-w-6xl'}`}
    >
      {children}
    </div>
  );
}
